<script setup lang="ts">
import { computed } from 'vue'

import emailSource from './assets/email.png'
import slackSource from './assets/slack.png'
import teamsSource from './assets/teams.png'
import whatsappSource from './assets/whatsapp.png'
import {
  normalizeNotificationProvider,
  type NotificationProvider,
  type NotificationProviderKey,
} from './types'

const props = withDefaults(
  defineProps<{
    provider: NotificationProviderKey
    size?: number
    /** Omit for a decorative icon; provide when the icon is the only accessible name. */
    label?: string
  }>(),
  { size: 20 },
)

const sources: Readonly<Record<NotificationProvider, string>> = {
  email: emailSource,
  slack: slackSource,
  whatsapp: whatsappSource,
  teams: teamsSource,
}

const normalizedProvider = computed(() => normalizeNotificationProvider(props.provider))
const source = computed(() => sources[normalizedProvider.value])
const dimensions = computed(() => ({
  inlineSize: `${props.size}px`,
  blockSize: `${props.size}px`,
}))
</script>

<template>
  <span
    class="notification-provider-icon inline-flex flex-none items-center justify-center overflow-hidden"
    :data-provider="normalizedProvider"
    :data-testid="`notification-provider-icon-${normalizedProvider}`"
    :style="dimensions"
    :role="label ? 'img' : undefined"
    :aria-label="label || undefined"
    :aria-hidden="label ? undefined : 'true'"
  >
    <img :src="source" alt="" aria-hidden="true" />
  </span>
</template>

<style scoped>
.notification-provider-icon img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.notification-provider-icon[data-provider='email'] img {
  transform: scale(1.35);
}
</style>
